import { AssessmentSelectDto } from '@@types/api.types';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { cn } from '@lib/utils';
import { useGetTechnologyUsesWithAssessments } from '@queries/technology-uses.query';
import { formatAssessmentDate } from '@utils/assessment.util';
import { MRL_TRL_SETTINGS } from '@utils/constants';
import { ChevronDownIcon } from 'lucide-react';
import { FunctionComponent, useState } from 'react';

interface TechnologyTrlDetailsProps {
  featuredAssessment: AssessmentSelectDto;
  className?: string;
}

export const TechnologyTrlDetails: FunctionComponent<TechnologyTrlDetailsProps> = (props) => {
  const { featuredAssessment, className } = props;
  const [isSubdomainsExpanded, setIsSubdomainsExpanded] = useState(false);

  const { data: technologyUses } = useGetTechnologyUsesWithAssessments(featuredAssessment.technologyId);

  const subdomains =
    technologyUses
      ?.find((use) => use.id === featuredAssessment.use.id)
      ?.domains.find((domain) => domain.id === featuredAssessment.domain.id)?.subdomains ?? [];

  const toggleSubdomains = () => {
    setIsSubdomainsExpanded((prev) => !prev);
  };

  const getTrlDescription = (score: number | undefined | null): { label: string; description: string } | null => {
    if (!score || score === 0) return null;

    const levelData = MRL_TRL_SETTINGS.trl.levels.find((level) => level.level === score);
    return levelData ? { label: levelData.label, description: levelData.description } : null;
  };

  const trlDescription = getTrlDescription(featuredAssessment.score);

  return (
    <div className={cn('font-light text-sm space-y-3 text-[#1F1C1AB2] rounded-md w-full', className)}>
      {trlDescription && (
        <div className="mb-4 px-2">
          <div className="text-sm font-medium text-gray-600 mb-1">
            TRL {featuredAssessment.score} - {trlDescription.label}
          </div>
          <div className="text-xs text-gray-500 leading-[18px]">{trlDescription.description}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="px-2">
          <TextWithLabel
            title={featuredAssessment.use.name}
            label="Use"
            labelClassName="text-xs"
            className="text-sm flex-col-reverse"
          />
        </div>
        <div className="px-2">
          <TextWithLabel
            title={featuredAssessment.domain.name}
            label="Domain"
            labelClassName="text-xs"
            className="text-sm flex-col-reverse"
          />
        </div>
      </div>

      <div className="px-2">
        <TextWithLabel
          title={formatAssessmentDate(featuredAssessment)}
          label="Assessment Date"
          labelClassName="text-xs"
          className="text-sm flex-col-reverse"
        />
      </div>

      {subdomains.length > 0 && (
        <>
          <div className="h-px border-t border-gray-200 my-3" />

          <div
            className="flex justify-between items-center cursor-pointer px-2 mt-4"
            onClick={toggleSubdomains}
          >
            <div className="text-xs font-normal text-[#1F1C1AB2]">
              {subdomains.length} Subdomain
              {subdomains.length === 1 ? '' : 's'}
            </div>
            <ChevronDownIcon
              size={20}
              className={cn('text-[#1F1C1AB2] transition-transform duration-200', isSubdomainsExpanded && 'rotate-180')}
            />
          </div>
        </>
      )}

      {isSubdomainsExpanded && subdomains.length > 0 && (
        <div className="mt-2 flex px-2 flex-col gap-0.5">
          {subdomains.map((subdomain) => (
            <div
              key={subdomain.id}
              className="flex flex-row items-center"
            >
              <div className="flex flex-col">
                <div className="text-gray-600 font-light text-xs">{subdomain.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
